import 'styles/quoteline.css';
import { useState, useEffect, useRef } from "react";
import GetPDF from 'components/GetPDF';
import QuoteDuplicatePopup from 'components/QuoteDuplicatePopup';
import api from "api";

const QuoteLine = (
    {
        quoteid,  // id du devis
        setSelectedQuote, // pour sélectionner le devis
        setEditQuote, // pour ouvrir le mode édition
        setStaff, // pour ouvrir l'édition du staff
        setDelivery, // pour ouvrir l'édition des frais de transport
        setDeliveryBill, // pour ouvrir l'édition du bon de livraison
        setReloadQuotes, // pour recharger la liste des devis
        printCancelled,
    }
) => {

    const [ quote, setQuote ] = useState(null); // objet devis
    const [ cancelled, setCancelled ] = useState(null); // flag annulation
    const [ confirmed, setConfirmed ] = useState(null); // flag confirmation
    const [ billed, setBilled ] = useState(null); // flag facturation
    const [ showDuplicate, setShowDuplicate ] = useState(false); // flag popup duplication
    const [ summary, setSummary ] = useState(null); // résumé (montant HT) pour le tooltip
    const [ showTooltip, setShowTooltip ] = useState(false); // affichage différé du tooltip
    const tooltipTimer = useRef(null); // minuteur d'affichage du tooltip

    // création de l'objet devis avec calcul des dates min et max et positionnement des flags
    const reloadQuote = (q) => {
        q.first_date = new Date(q.first_date);
        q.last_date = new Date(q.last_date);
        setBilled(q.billed);
        setCancelled(q.cancelled);
        setConfirmed(q.confirmed);
        setQuote(q);
    };

    // récupération des données du devis
    useEffect(() => {
        api.get(`quote/${quoteid}`)
        .then(response => { reloadQuote(response.data); });
    }, [quoteid]);

    // gestion du clic annulation
    const handleClickA = () => {
        if (billed) {
            alert("Impossible de modifier un devis facturé");
            return;
        }
        if (confirmed) {
            alert("Impossible d'annuler un devis confirmé");
            return;
        }
        if (quote.cancelled) {
            setCancelled(false);
            api.put(`quote/${quote.id}/flag`, { cancelled : false })
            .then(response => { reloadQuote(response.data); });
        } else {
            setCancelled(true);
            api.put(`quote/${quote.id}/flag`, { cancelled : true })
            .then(response => { reloadQuote(response.data); });
        }
    }

    // gestion du clic confirmation
    const handleClickC = () => {
        if (billed) {
            alert("Impossible de modifier un devis facturé");
            return;
        }
        if (cancelled) {
            alert("Impossible de confirmer un devis annulé");
            return;
        }
        if (quote.confirmed) {
            setConfirmed(false);
            api.put(`quote/${quote.id}/flag`, { confirmed : false })
            .then(response => { reloadQuote(response.data); });
        } else {
            setConfirmed(true);
            api.put(`quote/${quote.id}/flag`, { confirmed : true })
            .then(response => { reloadQuote (response.data); });
        }
    }

    // gestion du clic facturation
    const handleClickF = () => {
        if (cancelled) {
            alert("Impossible de facturer un devis annulé");
            return;
        }
        if (quote.billed) {
            // eslint-disable-next-line no-restricted-globals
            const response = confirm("SÛR POUR LA SUPPRESSION DE LA FACTURE ?");
            if (!response) return;
            setBilled(false);
            api.put(`quote/${quote.id}/flag`, { billed : false })
            .then(response => { reloadQuote(response.data); });
        } else {
            setBilled(true);
            api.put(`quote/${quote.id}/flag`, { billed : true })
            .then(response => { reloadQuote (response.data); });
        }
    }

    // gestion de la suppression d'un devis
    const handleDel = () => {
        // eslint-disable-next-line no-restricted-globals
        const response = confirm("SÛR POUR LA SUPPRESSION ?");
        if (!response) return;
        api.delete(`quote/${quote.id}/del`)
        .then(()=>{
            setQuote(null);
        });
    }

    // gestion du clic édition
    const handleClickEvent = () => {
        setSelectedQuote(quote);
        setEditQuote(true, quote.id);
    }   

    // gestion du clic frais de transport
    const handleDelivery = () => {
        setSelectedQuote(quote);
        setDelivery(true, quote.id);
    };
    
    // gestion du clic staff
    const handelStaff = () => {
        setSelectedQuote(quote);
        setStaff(true, quote.id);
    };

    const handleClickDelivery = () => {
        setSelectedQuote(quote);
        setDeliveryBill(true);
    };

    // gestion du clic duplication
    const handleDuplicate = () => {
        setShowDuplicate(true);
    };

    // charge le résumé du devis (montant HT) au survol, une seule fois, et affiche le tooltip après un délai
    const handleMouseEnter = () => {
        if (!summary) {
            api.get(`quote/${quoteid}/summary`)
            .then(response => { setSummary(response.data); });
        }
        tooltipTimer.current = setTimeout(() => setShowTooltip(true), 600);
    };

    // annule l'affichage du tooltip si la souris quitte la ligne avant la fin du délai
    const handleMouseLeave = () => {
        clearTimeout(tooltipTimer.current);
        setShowTooltip(false);
    };

    // nettoyage du minuteur au démontage
    useEffect(() => {
        return () => clearTimeout(tooltipTimer.current);
    }, []);


    const options = { year: 'numeric', month: 'long', day: 'numeric' }; // utile pour toLocaleDateString 
    const now = new Date(); // pour calculer les 7 jours à partir de maintenant
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 jours à partir de maintenant

    return ( 
        <div id={`quote-${quoteid}`}>
        {quote
        &&
        (!quote.cancelled || printCancelled)
        &&
        <div
            className = { 
                `QuoteLine
                ${cancelled && "QuoteLineCancelled"}
                ${confirmed && !billed && "QuoteLineConfirmed"}
                ${billed && "QuoteLineBilled"}
            `}
        >   
            <div
                className= {
                    `QuoteLineText
                    ${quote.first_date <= sevenDaysFromNow && !cancelled && !billed && "QuoteLineSoon"}
                `}
            >
                {quote.first_date.toLocaleDateString('fr-FR', options)}
            </div>

            <div
                className= {
                    `QuoteLineText
                    ${quote.first_date <= sevenDaysFromNow && !cancelled && !billed && "QuoteLineSoon"}
                `}
                onClick={handleClickEvent}
            >
                {quote.last_date.toLocaleDateString('fr-FR', options)}
            </div>

            <div
                className='QuoteLineEvent'
                onClick={handleClickEvent}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <span className="QuoteLineText">{quote.event}</span>
                <div className={`QuoteLineTooltip ${showTooltip && "QuoteLineTooltipVisible"}`}>
                    <div className="QuoteLineTooltipRow">
                        <span className="QuoteLineTooltipLabel">Client</span>
                        <span>{quote.name || "—"}</span>
                    </div>
                    <div className="QuoteLineTooltipRow">
                        <span className="QuoteLineTooltipLabel">Téléphone</span>
                        <span>{quote.phone || "—"}</span>
                    </div>
                    <div className="QuoteLineTooltipRow">
                        <span className="QuoteLineTooltipLabel">Devis n°</span>
                        <span>{quote.id}</span>
                    </div>
                    <div className="QuoteLineTooltipRow">
                        <span className="QuoteLineTooltipLabel">Montant HT</span>
                        <span>{summary ? `${summary.amount_ht.toFixed(2)} €` : "…"}</span>
                    </div>
                    <div className="QuoteLineTooltipRow">
                        <span className="QuoteLineTooltipLabel">Remise</span>
                        <span>{summary ? `${summary.discount_percent.toFixed(1)} %` : "…"}</span>
                    </div>
                </div>
            </div>

            <div
                className='QuoteLineIcon'
                onClick={handleDelivery}
            >
                { quote.delivery ? "🚚" : "" } 
            </div>

            <div
                className='QuoteLineIcon'
                onClick={handelStaff}
            >
                🧑‍💼
            </div>

            <div
                className = "QuoteLineButton QuoteLineButtonA"
                onClick = {handleClickA}
            >
                A
            </div>

            <div
                className = "QuoteLineButton QuoteLineButtonC"
                onClick = {handleClickC}
            >   C
            </div>

            <div
                className = "QuoteLineButton QuoteLineButtonF"
                onClick = {handleClickF}
            >   F
            </div>

            <div></div>
            
            <div
                className='QuoteLineIcon'
                onClick={handleClickDelivery}
            >
                { !billed && confirmed && "📦" }
            </div>

            <GetPDF
                quoteid={quoteid}
            />

            <div
                className='QuoteLineIcon'
                onClick={handleDuplicate}
                title="Dupliquer le devis"
            >
                ⧉
            </div>

            <div
                className='QuoteLineIcon'
                onClick={handleDel}
            >
                { (!billed && !confirmed) ? "🗑️" : "" }
            </div>

        </div>
        }
        {
            showDuplicate
            &&
            <QuoteDuplicatePopup
                quoteId={quoteid}
                onClose={() => setShowDuplicate(false)}
                setReloadQuotes={setReloadQuotes}
            />
        }
        </div>
    )
}

export default QuoteLine;